import React, { useState } from 'react';
import { Form, Button, ListGroup, Badge, Modal } from 'react-bootstrap';

interface WorkflowStep {
  name: string;
  status: string;
  description?: string;
  previous_steps?: string[]; // Array de nomes dos passos anteriores
  allow_anytime_access?: boolean;
}

interface WorkflowBuilderProps {
  steps: WorkflowStep[];
  onChange: (steps: WorkflowStep[]) => void;
  workflowAreaType?: 'tickets' | 'purchases' | 'changes';
}

const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({ steps, onChange, workflowAreaType = 'tickets' }) => {
  const isPurchasesWorkflow = workflowAreaType === 'purchases';
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStepName, setNewStepName] = useState('');
  const [newStepStatus, setNewStepStatus] = useState('em_andamento');
  const [newStepDescription, setNewStepDescription] = useState('');
  const [newStepPreviousSteps, setNewStepPreviousSteps] = useState<string[]>([]);
  const [newStepAllowAnytimeAccess, setNewStepAllowAnytimeAccess] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const addStep = () => {
    if (!newStepName.trim()) {
      // Validação já feita pelo botão estar desabilitado, mas por segurança
      return;
    }
    
    // Validar que não há referências circulares
    if (editingIndex !== null) {
      const currentStepName = steps[editingIndex].name;
      if (newStepPreviousSteps.includes(currentStepName)) {
        // Nota: Este erro não deve ocorrer devido à lógica de filtro,
        // mas se ocorrer, será tratado silenciosamente
        console.warn('Não é possível selecionar o próprio passo como passo anterior.');
        return;
      }
    }
    
    const newStep: WorkflowStep = {
      name: newStepName,
      status: newStepStatus,
      description: newStepDescription || undefined,
      previous_steps: newStepPreviousSteps.length > 0 ? [...newStepPreviousSteps] : undefined,
      allow_anytime_access: isPurchasesWorkflow ? newStepAllowAnytimeAccess : undefined,
    };

    if (editingIndex !== null) {
      const updated = [...steps];
      updated[editingIndex] = newStep;
      onChange(updated);
      setEditingIndex(null);
    } else {
      onChange([...steps, newStep]);
    }

    setNewStepName('');
    setNewStepStatus('em_andamento');
    setNewStepDescription('');
    setNewStepPreviousSteps([]);
    setNewStepAllowAnytimeAccess(false);
    setShowAddModal(false);
  };

  const removeStep = (index: number) => {
    const stepToRemove = steps[index];
    // Remover o passo e também remover referências a ele em previous_steps de outros passos
    const updated = steps
      .filter((_, i) => i !== index)
      .map(step => {
        if (step.previous_steps && step.previous_steps.includes(stepToRemove.name)) {
          const newPreviousSteps = step.previous_steps.filter(name => name !== stepToRemove.name);
          return {
            ...step,
            previous_steps: newPreviousSteps.length > 0 ? newPreviousSteps : undefined
          };
        }
        return step;
      });
    onChange(updated);
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === steps.length - 1) return;

    const updated = [...steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  const editStep = (index: number) => {
    const step = steps[index];
    setNewStepName(step.name);
    setNewStepStatus(step.status);
    setNewStepDescription(step.description || '');
    setNewStepPreviousSteps(step.previous_steps || []);
    setNewStepAllowAnytimeAccess(!!step.allow_anytime_access);
    setEditingIndex(index);
    setShowAddModal(true);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6>Etapas do Fluxo</h6>
        <Button size="sm" variant="success" onClick={() => {
          setEditingIndex(null);
          setNewStepName('');
          setNewStepStatus('em_andamento');
          setNewStepDescription('');
          setNewStepPreviousSteps([]);
          setNewStepAllowAnytimeAccess(false);
          setShowAddModal(true);
        }}>
          + Adicionar Etapa
        </Button>
      </div>

      <ListGroup>
        {steps.map((step, index) => (
          <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Badge bg="primary" className="me-2">{index + 1}</Badge>
              <div>
                <strong>{step.name}</strong>
                <Badge bg="info" className="ms-2">{step.status}</Badge>
                {step.description && (
                  <small className="text-muted d-block">{step.description}</small>
                )}
                {step.previous_steps && step.previous_steps.length > 0 && (
                  <small className="text-muted d-block">
                    Passos anteriores: {step.previous_steps.join(', ')}
                  </small>
                )}
                {isPurchasesWorkflow && step.allow_anytime_access && (
                  <small className="text-success d-block">
                    Acesso livre: pode ser acessada de qualquer etapa.
                  </small>
                )}
              </div>
            </div>
            <div>
              <Button
                size="sm"
                variant="outline-primary"
                className="me-1"
                onClick={() => editStep(index)}
              >
                Editar
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                className="me-1"
                onClick={() => moveStep(index, 'up')}
                disabled={index === 0}
              >
                ↑
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                className="me-1"
                onClick={() => moveStep(index, 'down')}
                disabled={index === steps.length - 1}
              >
                ↓
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => removeStep(index)}
              >
                ×
              </Button>
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>

      {steps.length === 0 && (
        <p className="text-muted text-center py-3">Nenhuma etapa adicionada</p>
      )}

      <Modal show={showAddModal} onHide={() => {
        setShowAddModal(false);
        setEditingIndex(null);
        setNewStepPreviousSteps([]);
        setNewStepAllowAnytimeAccess(false);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingIndex !== null ? 'Editar Etapa' : 'Nova Etapa'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome da Etapa *</Form.Label>
            <Form.Control
              type="text"
              value={newStepName}
              onChange={(e) => setNewStepName(e.target.value)}
              placeholder="Ex: Análise Inicial"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status Associado</Form.Label>
            <Form.Select value={newStepStatus} onChange={(e) => setNewStepStatus(e.target.value)}>
              <option value="novo">Novo</option>
              <option value="aberto">Aberto</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="aguardando_informacao_usuario">Aguardando Informação do Usuário</option>
              <option value="aguardando_terceiros">Aguardando Terceiros</option>
              <option value="aguardando_evidencia">Aguardando Evidência</option>
              <option value="aguardando_problema">Aguardando Problema</option>
              <option value="aguardando_implantacao">Aguardando Implantação</option>
              <option value="resolvido">Resolvido</option>
              <option value="cancelado">Cancelado</option>
              <option value="fechado">Fechado</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição (Opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={newStepDescription}
              onChange={(e) => setNewStepDescription(e.target.value)}
              placeholder="Descrição da etapa..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Passos Anteriores (Opcional - para fluxos alternativos)</Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              Selecione os passos que devem preceder este passo. Deixe vazio para seguir a ordem sequencial.
            </Form.Text>
            {steps.length > 0 ? (
              <div>
                {steps.map((step, idx) => {
                  // Não permitir selecionar o próprio passo quando editando
                  if (editingIndex !== null && idx === editingIndex) return null;
                  // Não permitir selecionar passo com mesmo nome quando criando novo
                  if (editingIndex === null && newStepName && step.name === newStepName) return null;
                  
                  return (
                    <Form.Check
                      key={idx}
                      type="checkbox"
                      id={`previous-step-${idx}`}
                      label={step.name}
                      checked={newStepPreviousSteps.includes(step.name)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewStepPreviousSteps([...newStepPreviousSteps, step.name]);
                        } else {
                          setNewStepPreviousSteps(newStepPreviousSteps.filter(name => name !== step.name));
                        }
                      }}
                    />
                  );
                })}
                {steps.filter((step, idx) => {
                  if (editingIndex !== null && idx === editingIndex) return false;
                  if (editingIndex === null && newStepName && step.name === newStepName) return false;
                  return true;
                }).length === 0 && (
                  <p className="text-muted">Não há outros passos disponíveis para selecionar</p>
                )}
              </div>
            ) : (
              <p className="text-muted">Adicione outros passos primeiro para criar fluxos alternativos</p>
            )}
          </Form.Group>

          {isPurchasesWorkflow && (
            <Form.Group className="mb-3">
              <Form.Check
                id="allow-anytime-access"
                type="checkbox"
                label="Permitir acesso a qualquer momento do fluxo"
                checked={newStepAllowAnytimeAccess}
                onChange={(e) => setNewStepAllowAnytimeAccess(e.target.checked)}
              />
              <Form.Text className="text-muted">
                Quando marcado, esta etapa aparecerá como caminho disponível após qualquer outra etapa ainda não concluída.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAddModal(false);
            setEditingIndex(null);
            setNewStepPreviousSteps([]);
            setNewStepAllowAnytimeAccess(false);
          }}>
            Cancelar
          </Button>
          <Button variant="success" onClick={addStep}>
            {editingIndex !== null ? 'Salvar' : 'Adicionar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WorkflowBuilder;



